import { makeAutoObservable } from 'mobx';
import { IOAuthCredentialsParams } from '../../../../../models/IntegrationsTypes';
import { ICredentialsStore } from '../integration-store';

interface IEventMessage {
    type: string;
    [payloadKey: string]: any;
}

export interface IWhiplashCredentials {
    customer_id: string
    warehouse_id: string
    client_id: string
    client_secret: string
    user_id: string
}

const CLOSE_INTERVAL = 50;

class WhiplashOauthCredentialsStore implements ICredentialsStore {
    private _window = {} as Window;
    private _changes: IWhiplashCredentials = {} as IWhiplashCredentials;
    private _intervalId: any = undefined;
    private _messageHandler?: (event: IEventMessage) => void;

    constructor(private _credentialsParams: IOAuthCredentialsParams) {
        makeAutoObservable(this);
    }

    public get customerId() { return this._changes.customer_id; }
    public set customerId(v: string) { this._changes.customer_id = v; }

    public get warehouseId() { return this._changes.warehouse_id; }
    public set warehouseId(v: string) { this._changes.warehouse_id = v; }

    public get clientId() { return this._changes.client_id; }
    public set clientId(v: string) { this._changes.client_id = v; }

    public get clientSecret() { return this._changes.client_secret; }
    public set clientSecret(v: string) { this._changes.client_secret = v; }

    public get userId() { return this._changes.user_id; }
    public set userId(v: string) { this._changes.user_id = v; }

    // Handle case when user didn't finish authorization flow and closed window
    private _listenToWindowClosed(reject: (reason?: any) => void) {
        /** make sure to always clear interval before starting new one */
        clearInterval(this._intervalId);

        this._intervalId = setInterval(() => {
            if (!this._window.closed) {
                return;
            }
            clearInterval(this._intervalId);
            reject(new Error('Authorization interrupted, window was closed before finishing the process'));
        }, CLOSE_INTERVAL);
    }

    private _handleMessage(event: IEventMessage, resolve: (value: unknown) => void, reject: (reason?: any) => void) {
        if (event.data?.type !== 'OAUTH_RESPOSE') {
            return;
        }
        this._window.close();
        window.removeEventListener('message', this._messageHandler as (event: IEventMessage) => void, false);

        if (event.data.payload?.code) {
            return resolve({ ...this._changes, code: event.data.payload.code });
        }

        return reject(new Error('Failed to receive code'));
    }

    /** ICredentialsStore implementation ************/
    public async getCredentials() {

        if (this._messageHandler) {
            window.removeEventListener('message', this._messageHandler, false);
        }

        return new Promise((resolve, reject) => {
            const baseUrl = this._credentialsParams.is_sandbox ? 'https://sandbox.getwhiplash.com' : 'https://www.getwhiplash.com';
            const oauthUrl = `${baseUrl}/oauth/authorize?scope=user_manage&response_type=code&redirect_uri=${this._credentialsParams.callback_url}&client_id=${this._changes.client_id}`;
            this._window = window.open(oauthUrl, '', 'width=800,height=600,resizeable,scrollbars') as Window;

            this._messageHandler = (event: IEventMessage) => {
                this._handleMessage(event, resolve, reject);
            };

            window.addEventListener('message', this._messageHandler, false);

            this._listenToWindowClosed(reject);
        });
    }

    /********* ISaveable implementation *************/
    public hasChanges(): boolean {
        return !!Object.keys(this._changes).length;
    }

    public discardChanges(): void {
        this._changes = {} as IWhiplashCredentials;
    }
}

export default WhiplashOauthCredentialsStore;
